import { Component } from "@theme/component";
import { MegaMenuHoverEvent } from "@theme/events";
import { debounce, onDocumentLoaded, readDurationToken, setHeaderMenuStyle } from "@theme/utilities";

const SUBMENU_HEIGHT = "--submenu-height";
const OVERFLOW_INLINE_START = "--header-menu-overflow-inline-start";
const ARIA_EXPANDED_ATTRIBUTE = "aria-expanded";
const ARIA_FALSE = "false";

// Header-only intent gates — NOT animation timing. These remain long enough to
// filter pointer transit while keeping the compact navigation responsive.
const ACTIVATE_BUFFER_FALLBACK_MS = 75;
const DEACTIVATE_BUFFER_FALLBACK_MS = 90;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ["overflowMenu"];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#positionOverflowMenu();
    setHeaderMenuStyle();
  }, 100);

  /** @type {number | undefined} */
  #activateTimer;
  /** @type {number | undefined} */
  #deactivateTimer;

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {number | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = event => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) {
      return;
    }

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (!moving) {
      this.#reconcilePointerTarget();
      return;
    }
    this.#pointerIdleTimer = setTimeout(() => {
      if (!this.#state.activeItem) {
        return;
      }
      this.#state.activeItem.dataset.safetyBox = "false";
      this.#reconcilePointerTarget();
    }, 50);
  };

  /**
   * Activate the selected menu item after the short pointer-intent delay.
   * Keyboard users open submenu disclosures explicitly with Space.
   * @param {PointerEvent} event
   */
  activate = event => {
    clearTimeout(this.#activateTimer);
    clearTimeout(this.#deactivateTimer);

    this.#activateTimer = setTimeout(
      () => this.#doActivate(event),
      readDurationToken("--header-menu-hover-activate-buffer", ACTIVATE_BUFFER_FALLBACK_MS, this),
    );
  };

  /**
   * Perform the actual menu item activation.
   * @param {Event} event
   * @param {EventTarget | null} [activationTarget] - Element that owns the disclosure interaction.
   */
  #doActivate = (event, activationTarget = event.target) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(activationTarget instanceof Element) || !this.headerComponent) {
      return;
    }

    const item = findMenuItem(activationTarget);

    if (!item || item === this.#state.activeItem) {
      return;
    }

    if (item.parentElement) {
      delete item.parentElement.dataset.keyboardClosed;
    }
    const isDefaultSlot = activationTarget.slot === "";

    this.#syncOverflowActivation(isDefaultSlot);

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      setMenuItemExpanded(previouslyActiveItem, false);
    }

    this.#state.activeItem = item;
    this.ariaExpanded = "true";
    setMenuItemExpanded(item, true);

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = "";

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight <= 0) {
              return;
            }
            this.headerComponent?.style.setProperty(SUBMENU_HEIGHT, `${submenu.offsetHeight}px`);
            this.#cleanupMutationObserver();
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty(SUBMENU_HEIGHT, `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty("--submenu-opacity", "1");
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force] - Close even while an overflow surface is hovered.
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    clearTimeout(this.#activateTimer);

    if (!item || item !== this.#state.activeItem) {
      return;
    }

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(":hover"))) {
      return;
    }

    this.headerComponent?.style.setProperty(SUBMENU_HEIGHT, "0px");
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty("--submenu-opacity", "0");
    this.dataset.overflowExpanded = ARIA_FALSE;

    const submenu = findSubmenu(item);

    document.body.removeEventListener("pointermove", this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = ARIA_FALSE;
    setMenuItemExpanded(item, false);
    this.overflowTrigger?.setAttribute(ARIA_EXPANDED_ATTRIBUTE, ARIA_FALSE);

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach(image => image.removeAttribute("loading"));
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) {
        return;
      }
      const listItem = target.closest(".menu-list__list-item");
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent("pointerenter", { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener("pointermove", this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute("data-submenu-overlap-bottom-row");
    const boundary = isOverlap ? this.headerComponent?.querySelector(".header__row--top") : this.headerComponent;
    item.style.setProperty("--box-height", `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty("--box-height");
    delete item.dataset.safetyBox;
  }

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener("resize", this.#resizeListener);
    this.overflowMenu?.addEventListener("pointerleave", this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#activateTimer);
    clearTimeout(this.#deactivateTimer);
    this.#resizeListener.cancel();
    window.removeEventListener("resize", this.#resizeListener);
    document.body.removeEventListener("pointermove", this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener("pointerleave", this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * The explicit disclosure control for links moved into the overflow panel.
   */
  get overflowTrigger() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[slot="more"] button'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(":hover") ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest("header-component"));
  }

  /**
   * Align the compact overflow panel's inline-start edge with the "More"
   * disclosure, matching the placement of ordinary top-level submenus.
   */
  #positionOverflowMenu() {
    const overflowList = this.refs.overflowMenu;
    const trigger = this.overflowTrigger;
    if (!(overflowList instanceof HTMLElement) || !trigger) {
      return;
    }

    const overflowRect = overflowList.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    if (overflowRect.width === 0 || triggerRect.width === 0) {
      return;
    }

    const isRtl = getComputedStyle(this).direction === "rtl";
    const inlineStart = isRtl ? overflowRect.right - triggerRect.right : triggerRect.left - overflowRect.left;
    overflowList.style.setProperty(OVERFLOW_INLINE_START, `${Math.max(0, inlineStart)}px`);
  }

  /**
   * Publish overflow state and position its panel when activation comes from a
   * slotted "More" item.
   * @param {boolean} isDefaultSlot
   */
  #syncOverflowActivation(isDefaultSlot) {
    const expanded = (!isDefaultSlot).toString();
    if (!isDefaultSlot) {
      this.#positionOverflowMenu();
    }
    this.dataset.overflowExpanded = expanded;
    this.overflowTrigger?.setAttribute(ARIA_EXPANDED_ATTRIBUTE, expanded);
  }

  /**
   * Toggle the overflow disclosure from its native button.
   * @param {MouseEvent} event
   */
  toggleOverflow(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const moreItem = event.target.closest('[slot="more"]');
    const item = findMenuItem(moreItem);
    if (!moreItem || !item) {
      return;
    }

    event.preventDefault();
    clearTimeout(this.#activateTimer);
    clearTimeout(this.#deactivateTimer);

    if (item === this.#state.activeItem) {
      this.#deactivate(item, true);
      return;
    }

    this.#doActivate(event, moreItem);
  }

  /**
   * Close the overflow disclosure from either its trigger or an overflow link.
   * @param {KeyboardEvent} event
   */
  handleOverflowKeyDown(event) {
    if (event.key !== "Escape" || this.dataset.overflowExpanded !== "true") {
      return;
    }

    const eventSource = event.composedPath()[0];
    if (!(eventSource instanceof Node) || !this.contains(eventSource) || !this.#state.activeItem) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.#deactivate(this.#state.activeItem, true);
    this.overflowTrigger?.focus({ preventScroll: true });
  }

  /**
   * Toggle a top-level submenu disclosure with Space, or close it with Escape.
   * Enter remains the link's native navigation action.
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const item = findMenuItem(event.target);
    if (!item?.hasAttribute("aria-controls")) {
      return;
    }

    if (event.key === "Escape") {
      if (item !== this.#state.activeItem) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (item.parentElement) {
        item.parentElement.dataset.keyboardClosed = "";
      }
      this.#deactivate(item, true);
      item.focus({ preventScroll: true });
      return;
    }

    if (event.key !== " ") {
      return;
    }

    const eventSource = event.composedPath()[0];
    if (eventSource !== item) {
      return;
    }

    event.preventDefault();
    if (item === this.#state.activeItem) {
      if (item.parentElement) {
        item.parentElement.dataset.keyboardClosed = "";
      }
      this.#deactivate(item, true);
      return;
    }

    clearTimeout(this.#deactivateTimer);
    this.#doActivate(event);
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === "blur" && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    // Blur deactivates immediately for keyboard accessibility.
    // Pointer events get a short buffer to allow diagonal mouse paths to the submenu.
    if (event.type === "blur") {
      this.#deactivate();
    } else {
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = setTimeout(
        () => this.#deactivate(),
        readDurationToken("--header-menu-hover-deactivate-buffer", DEACTIVATE_BUFFER_FALLBACK_MS, this),
      );
    }
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector("slot")?.assignedElements();
    if (!slottedMenuLinks) {
      return this.overflowMenu?.offsetHeight || 0;
    }

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = cb => {
      slottedMenuLinks.forEach(link => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus(submenu => {
      submenu.style.setProperty("display", "none");
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus(submenu => {
      submenu.style.removeProperty("display");
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) {
      return;
    }

    const isOverlapSituation = Object.hasOwn(this.headerComponent.dataset, "submenuOverlapBottomRow");

    // Cast hoisted to its own statement: an inline `/** @type */` cast inside the
    // ternary is fragile under Prettier reflow (it detaches from the querySelector
    // call and `.offsetHeight` falls back to `Element`).
    const topRow = /** @type {HTMLElement | null} */ (this.headerComponent.querySelector(".header__row--top"));
    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? (topRow?.offsetHeight ?? 0)
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty("--full-open-header-height", `${fullOpenHeaderHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get("header-menu")) {
  customElements.define("header-menu", HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) {
    return null;
  }

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Keep expanded state on actual disclosure links only. Simple links in the
 * overflow panel remain links; the "More" button reports that panel's state.
 * @param {HTMLElement} item
 * @param {boolean} expanded
 */
function setMenuItemExpanded(item, expanded) {
  if (!item.hasAttribute("aria-controls")) {
    item.removeAttribute(ARIA_EXPANDED_ATTRIBUTE);
    return;
  }

  item.ariaExpanded = expanded.toString();
}
